// ============================================================================
// client/frappe-maillet.ts — LES SIX FRAPPES DU CONTREPOIDS (06/08, v2)
// ----------------------------------------------------------------------------
// Retour de Claude-Alain sur la v1 : « on dirait 6x les mêmes. Développe les
// animations. Tu peux changer aussi les postures pour de meilleures variations.
// Varie les effets visuels (traînée, éclats...) ».
//
// IL AVAIT RAISON, ET LA CAUSE ÉTAIT STRUCTURELLE : les six partageaient les
// MÊMES deux poses extrêmes (armé → impact), la MÊME courbe de vitesse (linéaire)
// et le MÊME effet (un secteur plein + un bord d'attaque). Seule la couleur
// changeait. Six variations d'un même geste ne font pas six gestes.
//
// LA v2 DONNE À CHAQUE FRAPPE SES TROIS ÉTAGES :
//   1. SA CHORÉGRAPHIE — deux poses écrites pour elle (le pilon se dresse sur la
//      pointe des pieds, la faux part en fente basse, l'uppercut décolle) ;
//   2. SA COURBE DE VITESSE — l'abattage tombe (accélère), l'uppercut explose
//      puis décélère, le pilon monte lentement, MARQUE UN TEMPS, puis s'abat ;
//   3. SON EFFET — lame, croissant rasant, anneau à échos, gerbe montante,
//      onde de choc, ou double arc de papier. Ce sont eux qui les rendent
//      reconnaissables du premier coup d'œil.
//
// Le jeu en tire au sort TROIS parmi les six : `FRAPPES_EN_JEU`.
// ============================================================================

import { melange, type Pose } from './papier'

type Ctx = CanvasRenderingContext2D

export type Frappe = 0 | 1 | 2 | 3 | 4 | 5
export const FRAPPE_NOMS = [
  "L'abattage",      // 0 — la masse tombe, le sol encaisse
  'La faux',         // 1 — fente basse, balayage rasant
  'Le moulinet',     // 2 — un tour complet, trois échos
  "L'uppercut",      // 3 — il décolle, gerbe montante
  'Le pilon',        // 4 — lever, temps d'arrêt, écrasement
  'Le revers lancé', // 5 — vrille du buste, double arc de papier
] as const

/** LES TROIS RETENUES POUR LE JEU (tirage au sort par coup). Changer cette liste
 *  = changer le trio joué. En attendant son choix : les trois les plus distinctes. */
export const FRAPPES_EN_JEU: readonly Frappe[] = [0, 2, 3] // son choix du 06/08 : abattage, moulinet, uppercut

/** L'effet visuel, c'est LUI qui distingue une frappe d'une autre à l'œil. */
export type EffetFrappe = 'lame' | 'rasant' | 'anneau' | 'gerbe' | 'onde' | 'double'

export interface TempsFrappe {
  ang: number          // angle du maillet (dessiné debout, poigne en 0,0, tête en haut)
  depuis: number       // angle de DÉPART du balayage
  antihoraire: boolean
  teinte: string       // "r,g,b"
  corps: Pose
  dx: number; dy: number // ancrage du maillet, en fractions de case
  effet: EffetFrappe
  k: number            // la progression APRÈS courbe de vitesse (0..1) : les effets s'en servent
  brut: number         // la progression brute (0..1), pour les effets qui suivent le temps réel
  retour?: boolean     // true pendant la remontée à la garde : le coup est passé, plus aucun effet
}

/* ─────────────────────────── LES COURBES DE VITESSE ───────────────────────── */
const chute = (t: number): number => t * t                       // accélère : ça tombe
const fouet = (t: number): number => 1 - (1 - t) * (1 - t) * (1 - t) // explose puis freine
const doux = (t: number): number => t * t * (3 - 2 * t)          // s'installe et se pose
/** Le PILON : montée lente (45 %), TEMPS D'ARRÊT en l'air (15 %), écrasement (40 %). */
const pilon = (t: number): number => t < .45 ? (t / .45) * .42 : t < .60 ? .42 : .42 + ((t - .60) / .40) ** 1.7 * .58

/* ──────────────────────────── LES CHORÉGRAPHIES ───────────────────────────── */
// Chaque frappe a SES deux poses. Rien n'est partagé : c'est ce qui fait six gestes.
const P = (o: Partial<Pose>): Pose => ({
  bassinY: 0, bassinRot: 0, buste: [0, 0, 0], tete: [0, 0, 0],
  cuAV: .1, gnAV: .1, cuAR: -.1, gnAR: .1, epAV: 0, cdAV: -.3, epAR: 0, cdAR: -.3, foulard: .4, ...o,
} as Pose)

// 0 · ABATTAGE : cambré en arrière, puis plongée avant, genou avant écrasé.
const A0 = P({ nom: 'abattage armé', bassinY: -2, bassinRot: -.2, buste: [-7, -2, -.34], tete: [-6, -2, .2],
  cuAV: .42, gnAV: .1, cuAR: -.45, gnAR: .3, epAV: -1.35, cdAV: -.55, epAR: -2.35, cdAR: -.95, etire: 1.04, foulard: .25 })
const B0 = P({ nom: 'abattage impact', bassinY: 4, bassinRot: .26, buste: [8, 4, .56], tete: [9, 3, -.3],
  cuAV: -.8, gnAV: .95, cuAR: .85, gnAR: .35, epAV: 1.5, cdAV: -.25, epAR: .9, cdAR: -.35, ecrase: .95, foulard: 1.5 })

// 1 · FAUX : de profil ramassé, puis FENTE BASSE, jambe arrière tendue au loin.
const A1 = P({ nom: 'faux armé', bassinY: 3, bassinRot: -.1, buste: [-3, 3, -.16], tete: [-2, 3, .12],
  cuAV: .55, gnAV: .8, cuAR: -.5, gnAR: .7, epAV: -.5, cdAV: -1.1, epAR: -1.5, cdAR: -1.2, ecrase: .96, foulard: .3 })
const B1 = P({ nom: 'faux fente', bassinY: 11, bassinRot: .1, buste: [5, 11, .34], tete: [6, 10, -.18],
  cuAV: -1.0, gnAV: 1.15, cuAR: 1.1, gnAR: .12, epAV: 1.45, cdAV: -.2, epAR: 1.05, cdAR: -.28, ecrase: .9, foulard: 1.2 })

// 2 · MOULINET : le buste VRILLE, les deux bras suivent le tour, le corps s'étire à l'horizontale.
const A2 = P({ nom: 'moulinet armé', bassinY: -1, bassinRot: -.3, buste: [-4, -1, -.4], tete: [-5, -1, .3],
  cuAV: .5, gnAV: .2, cuAR: -.55, gnAR: .35, epAV: -2.2, cdAV: -.4, epAR: -1.3, cdAR: -.6, foulard: .5 })
const B2 = P({ nom: 'moulinet fin', bassinY: 1, bassinRot: .34, buste: [5, 1, .44], tete: [6, 1, -.32],
  cuAV: -.6, gnAV: .5, cuAR: .7, gnAR: .45, epAV: 1.6, cdAV: -.2, epAR: 2.3, cdAR: -.3, etire: .97, foulard: 1.7 })

// 3 · UPPERCUT : ramassé au sol comme un ressort, puis DÉCOLLAGE — il quitte le sol.
const A3 = P({ nom: 'uppercut ramassé', bassinY: 9, bassinRot: .06, buste: [2, 9, .3], tete: [2, 9, -.2],
  cuAV: -.75, gnAV: 1.7, cuAR: .6, gnAR: 1.8, epAV: .9, cdAV: -.9, epAR: .5, cdAR: -1.0, ecrase: .9, foulard: .2 })
const B3 = P({ nom: 'uppercut envol', bassinY: -7, bassinRot: -.14, buste: [1, -7, -.24], tete: [2, -7, .18],
  cuAV: -.1, gnAV: .22, cuAR: .28, gnAR: 1.0, epAV: -2.5, cdAV: -.2, epAR: -1.5, cdAR: -.45, etire: 1.12, air: true, solW: 0, foulard: 1.4 })

// 4 · PILON : sur la POINTE des pieds, les deux bras au ciel — puis écrasement total.
const A4 = P({ nom: 'pilon levé', bassinY: -5, bassinRot: 0, buste: [0, -5, 0], tete: [0, -5, 0],
  cuAV: .06, gnAV: .06, cuAR: -.06, gnAR: .06, epAV: -2.55, cdAV: -.1, epAR: -2.65, cdAR: -.1, etire: 1.1, foulard: .8 })
const B4 = P({ nom: 'pilon écrasement', bassinY: 10, bassinRot: 0, buste: [2, 10, .3], tete: [2, 10, -.2],
  cuAV: -.7, gnAV: 1.55, cuAR: .55, gnAR: 1.65, epAV: .75, cdAV: -.15, epAR: .7, cdAR: -.2, ecrase: .86, foulard: 1.3 })

// 5 · REVERS : buste VRILLÉ à contre-sens, bras croisé — puis ouverture totale.
const A5 = P({ nom: 'revers vrillé', bassinY: 0, bassinRot: .22, buste: [4, 0, .3], tete: [2, 0, -.24],
  cuAV: -.35, gnAV: .45, cuAR: .4, gnAR: .3, epAV: 1.55, cdAV: -1.15, epAR: .95, cdAR: -1.0, foulard: .3 })
const B5 = P({ nom: 'revers ouvert', bassinY: -1, bassinRot: -.24, buste: [-3, -1, -.3], tete: [-4, -1, .26],
  cuAV: .55, gnAV: .18, cuAR: -.6, gnAR: .55, epAV: -2.15, cdAV: -.15, epAR: -.9, cdAR: -.4, etire: 1.05, foulard: 1.6 })

/** Un instant de frappe, `t` de 0 (départ) à 1 (fin). PURE : la planche peint
 *  EXACTEMENT ce que le jeu peindra. */
export function tempsFrappe(f: Frappe, t: number): TempsFrappe {
  const brut = Math.max(0, Math.min(1, t))
  const D = -0.58 // l'angle de repos du contrepoids
  switch (f) {
    case 1: { const k = fouet(brut) // LA FAUX : rapide d'emblée, elle rase
      return { ang: -2.35 + k * 3.5, depuis: -2.35, antihoraire: false, teinte: '150,255,180',
        corps: melange(A1, B1, k), dx: .16 + k * .16, dy: .76 + k * .10, effet: 'rasant', k, brut } }
    case 2: { const k = doux(brut)  // LE MOULINET : il s'installe, tourne, se pose
      return { ang: D + k * Math.PI * 2, depuis: D, antihoraire: false, teinte: '150,215,255',
        corps: melange(A2, B2, Math.min(1, k * 1.3)), dx: .20 + k * .08, dy: .50, effet: 'anneau', k, brut } }
    case 3: { const k = fouet(brut) // L'UPPERCUT : explosion puis freinage
      return { ang: -2.85 - k * 2.5, depuis: -2.85, antihoraire: true, teinte: '255,214,120',
        corps: melange(A3, B3, k), dx: .14 + k * .20, dy: .78 - k * .38, effet: 'gerbe', k, brut } }
    case 4: { const k = pilon(brut) // LE PILON : montée, TEMPS D'ARRÊT, écrasement
      const monte = brut < .60
      return monte
        ? { ang: D + (k / .42) * 0.58, depuis: D, antihoraire: false, teinte: '255,190,120',
            corps: melange(A0, A4, Math.min(1, brut / .45)), dx: .08, dy: .38 - Math.min(1, brut / .45) * .18, effet: 'onde', k, brut }
        : { ang: ((k - .42) / .58) * 3.05, depuis: 0, antihoraire: false, teinte: '255,140,70',
            corps: melange(A4, B4, (k - .42) / .58), dx: .08 + ((k - .42) / .58) * .18, dy: .20 + ((k - .42) / .58) * .56, effet: 'onde', k, brut } }
    case 5: { const k = chute(brut) // LE REVERS : lent au départ, fouetté à la fin
      return { ang: 2.0 - k * 3.5, depuis: 2.0, antihoraire: true, teinte: '225,170,255',
        corps: melange(A5, B5, k), dx: -.14 + k * .48, dy: .64 - k * .20, effet: 'double', k, brut } }
    default: { const k = chute(brut) // 0 · L'ABATTAGE : elle TOMBE, donc elle accélère
      return { ang: D + k * 2.55, depuis: D, antihoraire: false, teinte: '255,240,200',
        corps: melange(A0, B0, k), dx: .26 - k * .06, dy: .48 + k * .12, effet: 'lame', k, brut } }
  }
}

/**
 * LE RETOUR À LA GARDE (06/08, « est-ce que tu peux ajouter des frames pour que cela semble plus
 * fluide ? »). C'était LÀ que ça saccadait : la frappe se jouait sur cinq ticks, puis le corps
 * SAUTAIT d'un coup à la posture de repos pendant les cinq ticks suivants du rechargement. On
 * anime donc aussi la remontée : le personnage se replace, le maillet revient à sa garde, et le
 * cycle complet d'un coup devient continu du premier au dernier tick.
 *
 * `u` va de 0 (fin du coup) à 1 (garde retrouvée) ; `repos` décrit la posture de porteur.
 */
export function tempsRetour(f: Frappe, u: number, repos: { debout: Pose; ang: number; dx: number; dy: number }): TempsFrappe {
  const fin = tempsFrappe(f, 1)
  const k = doux(Math.max(0, Math.min(1, u))) // il se relève en s'installant, pas d'un ressort
  return {
    ...fin,
    corps: melange(fin.corps, repos.debout, k),
    // l'angle repart de la fin du coup vers la garde par le CHEMIN LE PLUS COURT : sans ça, le
    // moulinet (qui finit un tour complet plus loin) rembobinerait tout son cercle à l'envers.
    ang: fin.ang + court(repos.ang - fin.ang) * k,
    depuis: fin.ang, antihoraire: false,
    dx: fin.dx + (repos.dx - fin.dx) * k,
    dy: fin.dy + (repos.dy - fin.dy) * k,
    k, brut: 1, retour: true, // le coup est passé : plus aucune traînée pendant la remontée
  }
}

/** Ramène un écart d'angle dans (-π, π] : le plus court chemin sur le cercle. */
function court(d: number): number {
  let a = d % (Math.PI * 2)
  if (a > Math.PI) a -= Math.PI * 2
  if (a <= -Math.PI) a += Math.PI * 2
  return a
}

/* ══════════════════════════ LES EFFETS VISUELS ══════════════════════════════
 * Le repère est celui du maillet : origine à la POIGNE, l'arme dessinée debout
 * vers le haut, l'axe X vers l'avant du personnage. `T` = longueur du maillet.
 * Aucun aléa : tout est fonction de la progression, donc identique sur tous
 * les écrans et rejouable. */
export function peintEffetFrappe(g: Ctx, tf: TempsFrappe, T: number): void {
  if (tf.retour) return // remontée à la garde : le geste est fini, on ne traîne plus rien
  const { teinte: c, k, brut, ang, depuis, antihoraire: anti } = tf
  const fade = 1 - brut * 0.7
  const arc = (r: number, a: number, ep: number): void => {
    g.strokeStyle = `rgba(${c},${a.toFixed(3)})`; g.lineWidth = ep; g.lineCap = 'round'
    g.beginPath(); g.arc(0, 0, r, depuis - Math.PI / 2, ang - Math.PI / 2, anti); g.stroke()
  }
  const secteur = (r: number, a: number): void => {
    g.fillStyle = `rgba(${c},${a.toFixed(3)})`
    g.beginPath(); g.moveTo(0, 0); g.arc(0, 0, r, depuis - Math.PI / 2, ang - Math.PI / 2, anti); g.closePath(); g.fill()
  }
  const tete = (a: number): [number, number] => [Math.sin(a) * T, -Math.cos(a) * T]

  switch (tf.effet) {
    case 'rasant': {
      // LA FAUX : pas de secteur du tout — une LAME fine et très longue au ras du sol,
      // doublée d'une traîne de poussière qui court devant la tête.
      arc(T * 1.5, 0.9 * fade, 3)
      arc(T * 1.28, 0.35 * fade, 7)
      const [hx, hy] = tete(ang)
      g.fillStyle = `rgba(226,216,194,${(0.5 * fade).toFixed(3)})`
      for (let i = 0; i < 5; i++) {
        const d = i * 5 + k * 16
        g.beginPath(); g.ellipse(hx + d, hy + 3 + Math.sin(i) * 2, 4 - i * 0.5, 2.2, 0, 0, Math.PI * 2); g.fill()
      }
      break
    }
    case 'anneau': {
      // LE MOULINET : un ANNEAU complet (il tourne, la trace se referme) plus TROIS ÉCHOS
      // rémanents de la tête d'acier aux positions précédentes — la signature du tour.
      g.strokeStyle = `rgba(${c},${(0.55 * fade).toFixed(3)})`; g.lineWidth = 3
      g.beginPath(); g.arc(0, 0, T * 1.15, 0, Math.PI * 2); g.stroke()
      arc(T * 1.3, 0.95 * fade, 5)
      for (let i = 1; i <= 3; i++) {
        const [ex, ey] = tete(ang - (anti ? -1 : 1) * i * 0.55)
        g.fillStyle = `rgba(${c},${(0.3 * fade / i).toFixed(3)})`
        g.beginPath(); g.ellipse(ex, ey, 9 - i * 1.6, 5 - i * 0.8, ang, 0, Math.PI * 2); g.fill()
      }
      break
    }
    case 'gerbe': {
      // L'UPPERCUT : la traînée MONTE, et une gerbe d'étincelles jaillit vers le ciel
      // depuis la tête. Rien au sol : tout part vers le haut, c'est son sujet.
      arc(T * 1.35, 0.85 * fade, 4)
      secteur(T * 1.1, 0.16 * fade)
      const [hx, hy] = tete(ang)
      g.save(); g.globalCompositeOperation = 'lighter'
      for (let i = 0; i < 7; i++) {
        const a = -Math.PI / 2 + (i - 3) * 0.2
        const d = 6 + k * 30 + i % 3 * 5
        const ex = hx + Math.cos(a) * d, ey = hy + Math.sin(a) * d
        g.globalAlpha = 0.8 * fade * (1 - i / 9)
        g.fillStyle = i % 2 ? '#fff3c4' : '#ffd23f'
        g.fillRect(ex - 1.2, ey - 5, 2.4, 10)
      }
      g.restore()
      break
    }
    case 'onde': {
      // LE PILON : RIEN pendant la montée, sinon un halo qui se charge sur la tête d'acier
      // (l'anticipation, c'est le silence) ; puis, à l'écrasement, une ONDE DE CHOC au sol
      // et des éclats qui giclent. Le contraste est tout l'effet.
      const [hx, hy] = tete(ang)
      if (brut < .60) {
        const ch = brut / .60
        g.save(); g.globalCompositeOperation = 'lighter'
        const grad = g.createRadialGradient(hx, hy, 0, hx, hy, 10 + ch * 16)
        grad.addColorStop(0, `rgba(${c},${(0.5 * ch).toFixed(3)})`)
        grad.addColorStop(1, `rgba(${c},0)`)
        g.fillStyle = grad
        g.beginPath(); g.arc(hx, hy, 10 + ch * 16, 0, Math.PI * 2); g.fill()
        g.restore()
      } else {
        const e = (brut - .60) / .40
        arc(T * 1.2, 0.7 * (1 - e), 5)
        g.strokeStyle = `rgba(${c},${(0.85 * (1 - e)).toFixed(3)})`; g.lineWidth = 3
        g.beginPath(); g.ellipse(hx, hy + 4, 8 + e * 46, (8 + e * 46) * 0.26, 0, 0, Math.PI * 2); g.stroke()
        g.fillStyle = `rgba(226,216,194,${(0.75 * (1 - e)).toFixed(3)})`
        for (let i = 0; i < 8; i++) {
          const a = Math.PI + (i / 7) * Math.PI
          const d = 8 + e * 34
          g.fillRect(hx + Math.cos(a) * d - 1.5, hy + 4 + Math.sin(a) * d * 0.5 - 1.5, 3.2, 3.2)
        }
      }
      break
    }
    case 'double': {
      // LE REVERS : DEUX arcs décalés (la masse et son sillage), plus des copeaux de papier
      // qui s'envolent derrière — le seul effet qui laisse quelque chose en l'air.
      arc(T * 1.4, 0.85 * fade, 4)
      g.save(); g.rotate(anti ? 0.22 : -0.22)
      arc(T * 1.16, 0.3 * fade, 3)
      g.restore()
      for (let i = 0; i < 6; i++) {
        const a = depuis + (ang - depuis) * (i / 6) * 0.9
        const [ex, ey] = tete(a)
        const vol = k * 14 * (1 + i % 3)
        g.save()
        g.globalAlpha = 0.6 * fade * (1 - i / 7)
        g.fillStyle = `rgba(${c},1)`
        g.translate(ex, ey - vol * 0.5)
        g.rotate(a + k * 3 + i)
        g.fillRect(-3, -2, 6, 4)
        g.restore()
      }
      break
    }
    default: {
      // L'ABATTAGE : une LAME pleine (secteur large + bord d'attaque très net), puis
      // l'impact au sol — poussière et cailloux qui sautent sous la tête.
      secteur(T * 1.24, 0.24 * fade)
      arc(T * 1.36, 0.95 * fade, 5)
      if (brut > .62) {
        const e = (brut - .62) / .38
        const [hx, hy] = tete(ang)
        g.fillStyle = `rgba(226,216,194,${(0.7 * (1 - e)).toFixed(3)})`
        for (let i = 0; i < 6; i++) {
          const a = Math.PI + (i / 5) * Math.PI
          const d = 5 + e * 26
          g.beginPath(); g.arc(hx + Math.cos(a) * d, hy + Math.sin(a) * d * 0.55, 3.2 - i * 0.3, 0, Math.PI * 2); g.fill()
        }
        g.strokeStyle = `rgba(255,240,200,${(0.6 * (1 - e)).toFixed(3)})`; g.lineWidth = 2
        g.beginPath(); g.ellipse(hx, hy + 2, 10 + e * 30, (10 + e * 30) * 0.22, 0, 0, Math.PI * 2); g.stroke()
      }
    }
  }
}
